import React, { useState, useEffect } from 'react';
import PublicLayout from './PublicLayout';
import SEO from '../common/SEO';
import { Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Events', 'Programs', 'Member Stories', 'Partnerships', 'Milestones', 'Impact', 'Research', 'Training'];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        console.error('API URL not configured');
        return;
      }
      const response = await fetch(`${API_BASE}/news/public?limit=50`);
      const data = await response.json();

      if (data.success) {
        // Map API data to match component expectations
        const mappedArticles = data.data.map(item => ({
          id: item.id,
          title: item.title,
          category: item.category,
          date: item.published_date,
          author: item.author,
          excerpt: item.excerpt,
          image: item.image_url,
          featured: item.featured
        }));
        setArticles(mappedArticles);
      } else {
        setError('Failed to load news');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = selectedCategory === 'All'
    ? articles
    : articles.filter(article => article.category === selectedCategory);

  return (
    <PublicLayout>
      <SEO
        title="Stories & News | Africa Respiratory Care Association"
        description="Stay updated with the latest news, stories, and announcements from ARCA — Africa Respiratory Care Association."
        keywords="ARCA news, respiratory care Africa, healthcare stories, ARCA announcements"
        image="/arcanews.logo.jpg"
        url="/news"
      />
      <div className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                <i className="fas fa-newspaper text-white" style={{ fontSize: '40px' }}></i>
              </div>
              <h1 className="display-4 fw-bold text-dark mb-4">Stories & News</h1>
              <p className="lead text-muted">
                Stay updated with the latest from ARCA - inspiring stories, news, and updates
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-4">
            <div className="d-flex flex-wrap justify-content-center gap-3">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`btn ${selectedCategory === category
                    ? 'text-white'
                    : 'btn-outline-secondary'
                    } rounded-pill px-4`}
                  style={selectedCategory === category ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : {}}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-warning text-center" role="alert">
              {error}
            </div>
          )}

          {/* No Articles State */}
          {!loading && !error && articles.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">No news articles available at the moment.</p>
            </div>
          )}

          {/* Featured Article */}
          {!loading && !error && filteredArticles.find(a => a.featured) && (
            <div className="mb-5">
              {filteredArticles.filter(a => a.featured).map(article => {
                const API_BASE = process.env.REACT_APP_BACKEND_URL;
                if (!API_BASE) return null;
                const imageUrl = article.image && article.image.startsWith('/uploads/')
                  ? `${API_BASE}${article.image}`
                  : article.image;

                return (
                  <div key={article.id} className="card shadow border-0">
                    <div className="row g-0">
                      <div className="col-md-6">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={article.title}
                            style={{ height: '400px', width: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.style.background = 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)';
                            }}
                          />
                        ) : (
                          <div style={{ height: '400px', background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}></div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <div className="card-body p-4 p-lg-5">
                          <span className="badge text-white mb-3" style={{ backgroundColor: '#6B8E23' }}>
                            {article.category}
                          </span>
                          <h2 className="h3 fw-bold text-dark mb-3">{article.title}</h2>
                          <div className="d-flex align-items-center text-muted small mb-3">
                            <Calendar size={16} className="me-2" />
                            <span>{new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            <span className="mx-2">•</span>
                            <span>{article.author}</span>
                          </div>
                          <p className="text-muted mb-4">{article.excerpt}</p>
                          <Link
                            to={`/news/${article.id}`}
                            className="btn fw-bold text-white"
                            style={{ backgroundColor: '#6B8E23' }}
                          >
                            Read Full Story <ArrowRight size={16} className="ms-2" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Articles Grid */}
          {!loading && !error && (
            <div className="row g-4">
              {filteredArticles.filter(a => !a.featured).map(article => {
                const API_BASE = process.env.REACT_APP_BACKEND_URL;
                if (!API_BASE) return null;
                const imageUrl = article.image && article.image.startsWith('/uploads/')
                  ? `${API_BASE}${article.image}`
                  : article.image;

                return (
                  <div key={article.id} className="col-md-6 col-lg-4">
                    <div className="card shadow-sm border-0 h-100">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={article.title}
                          style={{
                            height: '200px',
                            width: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.style.background = 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)';
                          }}
                        />
                      ) : (
                        <div style={{ height: '200px', background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}></div>
                      )}
                      <div className="card-body p-4">
                        <span className="badge text-white mb-3" style={{ backgroundColor: '#6B8E23' }}>
                          {article.category}
                        </span>
                        <h3 className="h5 fw-bold text-dark mb-3">{article.title}</h3>
                        <div className="d-flex align-items-center text-muted small mb-3">
                          <Calendar size={14} className="me-2" />
                          <span>
                            {new Date(article.date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <p className="text-muted small mb-3" style={{ minHeight: '60px' }}>{article.excerpt.substring(0, 120)}...</p>
                        <Link
                          to={`/news/${article.id}`}
                          className="btn btn-sm text-white fw-semibold"
                          style={{ backgroundColor: '#6B8E23' }}
                        >
                          Read Full Story <ArrowRight size={14} className="ms-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Newsletter Signup */}
          <div className="card border-0 shadow mt-5" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
            <div className="card-body p-5 text-center text-white">
              <h2 className="h3 fw-bold mb-3">Subscribe to Our Newsletter</h2>
              <p className="mb-4">Get the latest ARCA news and stories delivered to your inbox</p>
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <div className="input-group">
                    <input
                      type="email"
                      placeholder="Your email address"
                      className="form-control"
                    />
                    <button className="btn btn-light fw-semibold">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default NewsPage;
